import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

// Types
export type IkigaiCategory = 'love' | 'good_at' | 'world_needs' | 'paid_for';

export interface IkigaiResponse {
  id: string;
  category: IkigaiCategory;
  response: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface IkigaiStatement {
  id: string;
  statement: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface IkigaiState {
  responses: {
    love: IkigaiResponse[];
    good_at: IkigaiResponse[];
    world_needs: IkigaiResponse[];
    paid_for: IkigaiResponse[];
  };
  statement: IkigaiStatement | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface IkigaiContextValue extends IkigaiState {
  initialize: () => Promise<void>;
  saveAllResponses: (responses: Array<{ category: IkigaiCategory; response: string; sort_order: number }>) => Promise<void>;
  saveStatement: (text: string) => Promise<void>;
  clear: () => void;
}

const IkigaiContext = createContext<IkigaiContextValue | null>(null);

export function IkigaiProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<IkigaiState>({
    responses: {
      love: [],
      good_at: [],
      world_needs: [],
      paid_for: [],
    },
    statement: null,
    loading: false,
    error: null,
    initialized: false,
  });

  // Encryption helpers
  const encryptResponse = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) {
      throw new Error('Encryption key not available');
    }
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptResponse = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) {
      throw new Error('Encryption key not available');
    }
    return decrypt(crypto.encryptionKey, encrypted);
  };

  // Initialize / fetch all data
  const initialize = async () => {
    // Don't initialize if encryption key isn't ready yet
    if (!crypto.encryptionKey) {
      console.log('Ikigai: Waiting for encryption key...');
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const result = await api.get<{
        responses: {
          love: IkigaiResponse[];
          good_at: IkigaiResponse[];
          world_needs: IkigaiResponse[];
          paid_for: IkigaiResponse[];
        };
        statement: IkigaiStatement | null;
      }>('/ikigai');

      // Decrypt all responses
      const decryptedResponses = {
        love: await Promise.all(
          result.responses.love.map(async (r) => ({
            ...r,
            response: await decryptResponse(r.response),
          }))
        ),
        good_at: await Promise.all(
          result.responses.good_at.map(async (r) => ({
            ...r,
            response: await decryptResponse(r.response),
          }))
        ),
        world_needs: await Promise.all(
          result.responses.world_needs.map(async (r) => ({
            ...r,
            response: await decryptResponse(r.response),
          }))
        ),
        paid_for: await Promise.all(
          result.responses.paid_for.map(async (r) => ({
            ...r,
            response: await decryptResponse(r.response),
          }))
        ),
      };

      // Decrypt statement if exists
      let decryptedStatement: IkigaiStatement | null = null;
      if (result.statement) {
        decryptedStatement = {
          ...result.statement,
          statement: await decryptResponse(result.statement.statement),
        };
      }

      setState({
        responses: decryptedResponses,
        statement: decryptedStatement,
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error: any) {
      // 404 is normal - user hasn't created Ikigai yet
      if (error?.message?.includes('404')) {
        setState(s => ({
          ...s,
          loading: false,
          error: null,
          initialized: true,
        }));
      } else {
        console.error('Failed to initialize ikigai:', error);
        setState(s => ({
          ...s,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load Ikigai data',
          initialized: true, // Prevent infinite retry loops
        }));
      }
    }
  };

  // Save all responses (bulk replace pattern)
  const saveAllResponses = async (
    responses: Array<{ category: IkigaiCategory; response: string; sort_order: number }>
  ) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      // Encrypt all responses before sending
      const encryptedResponses = await Promise.all(
        responses.map(async (r) => ({
          category: r.category,
          response: await encryptResponse(r.response),
          sort_order: r.sort_order,
        }))
      );

      const result = await api.post<{
        responses: IkigaiResponse[];
      }>('/ikigai/responses', { responses: encryptedResponses });

      // Decrypt and group responses by category
      const allDecryptedResponses = await Promise.all(
        result.responses.map(async (r) => ({
          ...r,
          response: await decryptResponse(r.response),
        }))
      );

      const decryptedResponses = {
        love: allDecryptedResponses.filter(r => r.category === 'love'),
        good_at: allDecryptedResponses.filter(r => r.category === 'good_at'),
        world_needs: allDecryptedResponses.filter(r => r.category === 'world_needs'),
        paid_for: allDecryptedResponses.filter(r => r.category === 'paid_for'),
      };

      setState(s => ({
        ...s,
        responses: decryptedResponses,
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to save responses',
      }));
    }
  };

  // Save statement
  const saveStatement = async (text: string) => {
    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const encryptedText = await encryptResponse(text);
      const result = await api.post<{ statement: IkigaiStatement }>('/ikigai/statement', {
        statement: encryptedText,
      });

      const decryptedStatement = {
        ...result.statement,
        statement: await decryptResponse(result.statement.statement),
      };

      setState(s => ({
        ...s,
        statement: decryptedStatement,
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to save statement',
      }));
    }
  };

  // Clear on logout
  const clear = () => {
    setState({
      responses: {
        love: [],
        good_at: [],
        world_needs: [],
        paid_for: [],
      },
      statement: null,
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const value: IkigaiContextValue = {
    ...state,
    initialize,
    saveAllResponses,
    saveStatement,
    clear,
  };

  return <IkigaiContext.Provider value={value}>{children}</IkigaiContext.Provider>;
}

export function useIkigai() {
  const context = useContext(IkigaiContext);
  if (!context) {
    throw new Error('useIkigai must be used within IkigaiProvider');
  }
  return context;
}
